import { AxisCode } from '@atlas/types'
import { Button } from '@atlas/ui-kit'
import { Combobox } from '@atlas/ui-primitives'
import { CAMPAIGN_LIST_MAX_LIMIT, NON_ARCHIVED_CAMPAIGN_STATUSES } from '@/domain/campaign.types'
import type { WorkOrder } from '@/domain/work-order.types'
import { useCampaigns, useCreateCampaign } from '@/hooks/useCampaigns'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { campaignOptions } from '../../WorkOrders/new/form-model'

/** The campaign picker: the tenant's live campaigns, or a new one named after the order. */
export function CampaignPicker({
	workOrder: wo,
	value,
	onChange,
}: {
	workOrder: WorkOrder
	value: string | null
	onChange?: (campaignCode: string | null) => void
}) {
	const { data: campaigns = [] } = useCampaigns(
		{ status: NON_ARCHIVED_CAMPAIGN_STATUSES },
		{ page: 1, limit: CAMPAIGN_LIST_MAX_LIMIT },
	)
	const create = useCreateCampaign()
	const createFromOrder = () =>
		create.mutate(
			{
				name: wo.title,
				classification: {
					[AxisCode.BRAND]: wo.tuple?.[AxisCode.BRAND] ?? [],
					[AxisCode.GEOGRAPHY]: wo.tuple?.[AxisCode.GEOGRAPHY] ?? [],
				},
			},
			{
				onSuccess: (campaign) => {
					onChange?.(campaign.campaignCode)
					notification.success(`Campaign ${campaign.name} created.`)
				},
				onError: (error) => notification.error(apiErrorMessage(error, 'Could not create the campaign.')),
			},
		)
	return (
		<div className="flex flex-col gap-2">
			<Combobox
				aria-label="Campaign Name"
				options={campaignOptions(campaigns)}
				value={value}
				onChange={(code) => onChange?.(code ?? null)}
				isDisabled={!onChange}
				placeholder="Search a campaign…"
			/>
			{onChange ? (
				<Button variant="text-link" className="self-start" onPress={createFromOrder} isDisabled={create.isPending}>
					Create a campaign from this order
				</Button>
			) : null}
		</div>
	)
}
